import { createContext, useContext, ReactNode, useState, useEffect, useCallback } from 'react';

import { useInitializeCard, TCardUnitCreationParams, filterLimits } from '../hooks/useInitializeCard';
import { IPaymentCard, TCardLimits } from '../store';

import { TWalletSelector } from '@/features/cards/store/types/card.types';
import {
  TDashboardConfigSettingsCardsDesignType,
  IDashboardConfigSettingsCardsDesign,
} from '@/store/models/dashboard-config.model';
import { IUser } from '@/features/user/store/user.model';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-helpers';
import { CardService } from '@/features/cards/services/CardService';
import { useProfileContext } from '@/features/context/ProfileContext';
import { TLoadingStatus, loadingStatus } from '@/types/async';
import { selectProfileCardByUserIdAndCardId } from '@/store/selectors';

export interface ICardContext {
  card: IPaymentCard;
  updateCard: (cardField: Partial<TCardUnitCreationParams>) => void;
  limits: TCardLimits;
  designType: TDashboardConfigSettingsCardsDesignType;
  cardPrint: number;
  loadCard: (cardId: number) => void;
  status: TLoadingStatus;
}

export interface ICardCreationContext extends Omit<ICardContext, 'card' | 'limits'> {
  parentUser?: IUser;
  wallets: TWalletSelector[];
  designs: IDashboardConfigSettingsCardsDesign[];
  designType: TDashboardConfigSettingsCardsDesignType;
  card: TCardUnitCreationParams;
}

export interface ICardContextProps {
  parentUser?: IUser;
  children: ReactNode;
  wallets?: TWalletSelector[];
  paymentCard?: IPaymentCard;
  designType?: TDashboardConfigSettingsCardsDesignType;
  cardPrint?: number;
  onChange?: (key: string, value: string | number | boolean) => void;
}

export const CardContext = createContext<ICardContext>({} as ICardContext);
export const CardCreationContext = createContext<ICardCreationContext>({} as ICardCreationContext);

export function isCreationParam(data: TCardUnitCreationParams | IPaymentCard): data is TCardUnitCreationParams {
  return (data as TCardUnitCreationParams).cardPrint ? true : false;
}

export function CardProvider({
  designType,
  cardPrint,
  parentUser,
  wallets,
  children,
  paymentCard,
  onChange,
}: ICardContextProps) {
  const { user } = useProfileContext();
  const { designs, initialCard, currentDesign } = useInitializeCard({ designType });
  const dispatch = useAppDispatch();

  const [currentCard, setCard] = useState<IPaymentCard | undefined>(undefined);
  const [status, setStatus] = useState<TLoadingStatus>(loadingStatus.init);
  const [CardUnitCreationData, setCardUnitCreationData] = useState<TCardUnitCreationParams>(initialCard);
  const limits: Partial<TCardLimits> = currentCard ? filterLimits(currentCard) : {};

  const cardState = useAppSelector(
    (state) => selectProfileCardByUserIdAndCardId(state, paymentCard?.userId, paymentCard?.cardId)?.state,
  );

  const isLimitLoading = cardState?.actions?.Limits?.isLoading;
  const storeCard = cardState?.card;

  const handleUpdateCardCreationParams = useCallback(
    (field: Partial<TCardUnitCreationParams>) => {
      if (!!onChange) {
        onChange(Object.keys(field)[0], Object.values(field)[0]);
      } else {
        setCardUnitCreationData((prev) => ({ ...prev, ...field }));
      }
    },
    [onChange, setCardUnitCreationData],
  );

  const handleUpdateCard = useCallback(
    (field: Partial<IPaymentCard>) => {
      if (onChange) {
        onChange(Object.keys(field)[0], Object.values(field)[0]);
      }
    },
    [onChange],
  );

  const loadCard = useCallback(
    (cardId: number = undefined) => {
      if (cardId) {
        paymentCard.cardId = cardId;
      }
      setStatus(loadingStatus.init);
    },
    [paymentCard],
  );

  useEffect(() => {
    if (status === loadingStatus.init && paymentCard?.cardId) {
      setStatus(loadingStatus.loading);
      const fetchData = async () => {
        const cardDetails = await CardService.getCard(paymentCard.cardId);
        setCard(cardDetails);
        setStatus(loadingStatus.loaded);
        return;
      };
      if (!!paymentCard) {
        fetchData();
      }
    }
  }, [paymentCard, dispatch, status]);

  useEffect(() => {
    if (isLimitLoading === false) {
      setCard((prev) => ({
        ...prev,
        ...storeCard,
      }));
    }
  }, [isLimitLoading, cardState, storeCard, paymentCard]);

  if (user && !CardUnitCreationData?.userId) {
    CardUnitCreationData.userId = user.userId;
  }

  return paymentCard ? (
    <CardContext.Provider
      value={{ card: currentCard, updateCard: handleUpdateCard, limits, designType, cardPrint, loadCard, status }}
    >
      {children}
    </CardContext.Provider>
  ) : (
    <CardCreationContext.Provider
      value={{
        designType,
        parentUser,
        wallets,
        card: CardUnitCreationData,
        designs,
        cardPrint: currentDesign?.value ?? CardUnitCreationData?.cardPrint,
        updateCard: handleUpdateCardCreationParams,
        loadCard,
        status,
      }}
    >
      {children}
    </CardCreationContext.Provider>
  );
}

export function useCardContext(create?: false): ICardContext;
export function useCardContext(create: true): ICardCreationContext;
export function useCardContext(create: boolean): ICardContext | ICardCreationContext;
export function useCardContext(create = false) {
  const editContext = useContext(CardContext);
  const createContext = useContext(CardCreationContext);
  return create ? createContext : editContext;
}
